import { call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { clientApi } from '../api/client-api';
import { Client } from '../types/client-types';
import {
  createClientRequest,
  updateClientRequest,
  getClientsRequest,
  getClientByIdRequest,
  deleteClientRequest,
  updateClientStatusRequest,
  createClientSuccess,
  updateClientSuccess,
  getClientsSuccess,
  getClientByIdSuccess,
  deleteClientSuccess,
  updateClientStatusSuccess,
  clientFailure,
  CreateClientRequest,
  UpdateClientRequest,
  GetClientsRequest,
  GetClientByIdRequest,
  DeleteClientRequest,
  UpdateClientStatusRequest,
} from './client-slice';

// Mapper para transformar la respuesta de la API al tipo Client del frontend
// Soporta formato ClientEntity (name) y CRM LeadEntity (firstName+lastName)
function mapApiClientToClient(apiClient: any): Client {
  return {
    id: apiClient.id,
    name: apiClient.name || `${apiClient.firstName || ''} ${apiClient.lastName || ''}`.trim(),
    email: apiClient.email,
    phone: apiClient.phone || '',
    whatsapp: apiClient.secondaryPhone,
    type: apiClient.type || 'buyer',
    status: apiClient.status || apiClient.stage || 'prospect',
    budget: apiClient.budget ? parseFloat(apiClient.budget) : undefined,
    nationality: apiClient.nationality || '',
    language: 'es', // Default
    location: apiClient.city || apiClient.state || apiClient.country || '',
    amenities: [],
    funnelStage: apiClient.funnelStage || apiClient.stage || apiClient.referralSource || 'new_lead',
    createdAt: apiClient.createdAt ? new Date(apiClient.createdAt) : new Date(),
    lastInteraction: apiClient.updatedAt ? new Date(apiClient.updatedAt) : new Date(),
    notes: apiClient.notes,
    interactions: [],
    linkedProperties: [],
    documents: [],
    // Campos adicionales para el formulario de edición
    secondaryPhone: apiClient.secondaryPhone || '',
    birthDate: apiClient.birthDate,
    documentType: apiClient.documentType || '',
    documentNumber: apiClient.documentNumber || '',
    address: apiClient.address || '',
    city: apiClient.city || '',
    state: apiClient.state || '',
    zipCode: apiClient.zipCode || '',
    country: apiClient.country || '',
    source: apiClient.source || '',
    budgetCurrency: apiClient.budgetCurrency || 'USD',
    monthlyIncome: apiClient.monthlyIncome || 0,
    employmentStatus: apiClient.employmentStatus || '',
    company: apiClient.company || '',
    position: apiClient.position || '',
    propertyTypes: (() => {
      // Primero intentar obtener desde apiClient.propertyTypes (directo del backend)
      let types = Array.isArray(apiClient.propertyTypes) 
        ? apiClient.propertyTypes 
        : [];
      
      // Si no hay datos directos, intentar desde preferences
      if (types.length === 0) {
        types = Array.isArray(apiClient.preferences?.propertyTypes) 
          ? apiClient.preferences.propertyTypes 
          : (typeof apiClient.preferences?.propertyTypes === 'string' && apiClient.preferences.propertyTypes) 
            ? apiClient.preferences.propertyTypes.split(',').map((t: string) => t.trim()) 
            : [];
      }
      
      return types;
    })(),
    minPrice: apiClient.minPrice || apiClient.preferences?.minPrice || 0,
    maxPrice: apiClient.maxPrice || apiClient.preferences?.maxPrice || 0,
    minBedrooms: apiClient.minBedrooms || apiClient.preferences?.minBedrooms || 0,
    maxBedrooms: apiClient.maxBedrooms || apiClient.preferences?.maxBedrooms || 0,
    minBathrooms: apiClient.minBathrooms || apiClient.preferences?.minBathrooms || 0,
    maxBathrooms: apiClient.maxBathrooms || apiClient.preferences?.maxBathrooms || 0,
    minArea: apiClient.minArea || apiClient.preferences?.minArea || 0,
    maxArea: apiClient.maxArea || apiClient.preferences?.maxArea || 0,
    metadata: apiClient.metadata || {},
    // Campos de consentimiento ahora como columnas individuales
    consentMarketing: apiClient.consentMarketing || false,
    consentDataProcessing: apiClient.consentDataProcessing || false,
    consentThirdParty: apiClient.consentThirdParty || false,
    // Campos de contacto preferido ahora como columnas individuales
    preferredContactMethod: apiClient.preferredContactMethod || '',
    preferredContactTime: apiClient.preferredContactTime || '',
    preferredLocation: apiClient.preferredLocation || '',
    // Campos específicos para el formulario de edición
    assignedAgentId: apiClient.assignedAgentId || '',
    assignedAgent: apiClient.assignedAgent || '',
  } as any; // Cast temporal para evitar problemas de tipo con Date
}

// Saga para crear cliente
function* createClientSaga(
  action: PayloadAction<CreateClientRequest>
): Generator<any, void, any> {
  try {
    const { clientData } = action.payload;

    const response: any = yield call(clientApi.create, clientData);

    // Extraer los datos del cliente de la respuesta
    const responseData = response.data || response;

    const mappedClient = mapApiClientToClient(responseData);

    yield put(createClientSuccess(mappedClient));
  } catch (error: any) {
    yield put(
      clientFailure(
        error.response?.data?.message || 'Error al crear el cliente'
      )
    );
  }
}

// Saga para actualizar cliente
function* updateClientSaga(
  action: PayloadAction<UpdateClientRequest>
): Generator<any, void, any> {
  try {
    const { id, clientData } = action.payload;

    const response: any = yield call(clientApi.update, id, clientData);

    // Extraer los datos del cliente de la respuesta
    const responseData = response.data || response;

    const mappedClient = mapApiClientToClient(responseData);

    yield put(updateClientSuccess(mappedClient));
  } catch (error: any) {
    yield put(
      clientFailure(
        error.response?.data?.message || 'Error al actualizar el cliente'
      )
    );
  }
}

// Saga para obtener clientes
function* getClientsSaga(
  action: PayloadAction<GetClientsRequest>
): Generator<any, void, any> {
  try {
    const { filters, endpoint } = action.payload;

    // Usar endpoint personalizado si se proporciona, sino usar el por defecto
    const response: any = endpoint 
      ? yield call(clientApi.getAgencyClients, filters, endpoint)
      : yield call(clientApi.getAgencyClients, filters);

    const clientsData = response.data?.clients || response.clients || [];
    const paginationData = response.data?.pagination || response.pagination;

    // Mapear todos los clientes al tipo frontend
    const mappedClients = clientsData.map((client: any) => mapApiClientToClient(client));

    yield put(getClientsSuccess({
      clients: mappedClients,
      pagination: paginationData,
    }));
  } catch (error: any) {
    yield put(
      clientFailure(
        error.response?.data?.message || 'Error al obtener los clientes'
      )
    );
  }
}

// Saga para obtener cliente por ID
function* getClientByIdSaga(
  action: PayloadAction<GetClientByIdRequest>
): Generator<any, void, any> {
  try {
    const { id } = action.payload;

    const response: any = yield call(clientApi.getById, id);

    // Extraer los datos del cliente de la respuesta
    const clientData = response.data || response;

    const mappedClient = mapApiClientToClient(clientData);

    yield put(getClientByIdSuccess(mappedClient));
  } catch (error: any) {
    yield put(
      clientFailure(
        error.response?.data?.message || 'Error al obtener el cliente'
      )
    );
  }
}

// Saga para eliminar cliente
function* deleteClientSaga(
  action: PayloadAction<DeleteClientRequest>
): Generator<any, void, any> {
  try {
    const { id } = action.payload;
    
    yield call(clientApi.delete, id);
    
    yield put(deleteClientSuccess(id));
  } catch (error: any) {
    yield put(
      clientFailure(
        error.response?.data?.message || 'Error al eliminar el cliente'
      )
    );
  }
}

// Saga para actualizar estado del cliente
function* updateClientStatusSaga(
  action: PayloadAction<UpdateClientStatusRequest>
): Generator<any, void, any> {
  try {
    const { id, status } = action.payload;

    const response: any = yield call(clientApi.updateStatus, id, status);

    // Extraer los datos del cliente de la respuesta
    const responseData = response.data || response;

    const mappedClient = mapApiClientToClient(responseData);

    yield put(updateClientStatusSuccess(mappedClient));
  } catch (error: any) {
    yield put(
      clientFailure(
        error.response?.data?.message || 'Error al actualizar el estado del cliente'
      )
    );
  }
}

// Watcher sagas
export function* watchCreateClient() {
  yield takeLatest(createClientRequest.type, createClientSaga);
}

export function* watchUpdateClient() {
  yield takeLatest(updateClientRequest.type, updateClientSaga);
}

export function* watchGetClients() {
  yield takeLatest(getClientsRequest.type, getClientsSaga);
}

export function* watchGetClientById() {
  yield takeLatest(getClientByIdRequest.type, getClientByIdSaga);
}

export function* watchDeleteClient() {
  yield takeLatest(deleteClientRequest.type, deleteClientSaga);
}

export function* watchUpdateClientStatus() {
  yield takeLatest(updateClientStatusRequest.type, updateClientStatusSaga);
}

// Root saga para clientes
export function* clientSaga() {
  yield takeLatest(createClientRequest.type, createClientSaga);
  yield takeLatest(updateClientRequest.type, updateClientSaga);
  yield takeLatest(getClientsRequest.type, getClientsSaga);
  yield takeLatest(getClientByIdRequest.type, getClientByIdSaga);
  yield takeLatest(deleteClientRequest.type, deleteClientSaga);
  yield takeLatest(updateClientStatusRequest.type, updateClientStatusSaga);
}
